import { useAction, useMutation } from "convex/react";
import { AnimatePresence } from "motion/react";
import { useCallback, useState, type ChangeEventHandler } from "react";
import { useNavigate } from "react-router";

import { api } from "@convex/_generated/api";

import { GeneratedCard, type TemporaryCard } from "@/components/GeneratedCard";
import { Spinner } from "@/components/Spinner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useReviewTime } from "@/hooks/useReviewTime";

export const GenerateDeck = () => {
  const [prompt, setPrompt] = useState("");
  const [deckName, setDeckName] = useState<string>();
  const [deckDescription, setDeckDescription] = useState<string>();
  const [cards, setCards] = useState<TemporaryCard[]>([]);
  const [generationState, setGenerationState] = useState<
    "start" | "generating" | "error" | "generated"
  >("start");
  const [generationError, setGenerationError] = useState<string>();

  const generateDeckAction = useAction(api.deckGenerator.generateDeck);
  const generateDeck = useCallback(async () => {
    setGenerationState("generating");
    try {
      const generatedDeck = await generateDeckAction({ prompt });
      if (!generatedDeck) {
        setGenerationState("error");
        setGenerationError("Failed to generate deck");
        return;
      }
      setCards(generatedDeck.cards.map((card, i) => ({ ...card, tempId: i.toString() })));
      setDeckName(generatedDeck.name);
      setDeckDescription(generatedDeck.description);
      setGenerationState("generated");
    } catch (error) {
      console.error("Error generating deck:", error);
      setGenerationState("error");
      setGenerationError(JSON.stringify(error, null, 2));
    }
  }, [generateDeckAction, prompt]);

  const addDeckMutation = useMutation(api.decks.add);
  const reviewTime = useReviewTime();
  const navigate = useNavigate();
  const saveDeck = useCallback(async () => {
    if (!deckName) return;
    const deckId = await addDeckMutation({
      name: deckName,
      description: deckDescription,
      cards: cards.map((card) => ({
        question: card.question,
        answer: card.answer,
        nextReview: reviewTime,
      })),
    });
    await navigate(`/decks/${deckId}`);
  }, [addDeckMutation, cards, deckDescription, deckName, reviewTime, navigate]);

  const resetDeck = useCallback(() => {
    setGenerationState("start");
    setPrompt("");
    setDeckName(undefined);
    setDeckDescription(undefined);
    setCards([]);
  }, []);

  const handlePromptChange: ChangeEventHandler<HTMLTextAreaElement> = useCallback((event) => {
    setPrompt(event.target.value);
  }, []);

  const handleDeckNameChange: ChangeEventHandler<HTMLTextAreaElement> = useCallback((event) => {
    setDeckName(event.target.value);
  }, []);

  const updateQuestion = useCallback((tempId: string, newQuestion: string) => {
    setCards((prevCards) =>
      prevCards.map((card) => (card.tempId === tempId ? { ...card, question: newQuestion } : card))
    );
  }, []);

  const updateAnswer = useCallback((tempId: string, newAnswer: string) => {
    setCards((prevCards) =>
      prevCards.map((card) => (card.tempId === tempId ? { ...card, answer: newAnswer } : card))
    );
  }, []);

  const deleteCard = useCallback((tempId: string) => {
    setCards((prevCards) => prevCards.filter((card) => card.tempId !== tempId));
  }, []);

  return (
    <div className="flex h-full flex-col">
      <Card className="mb-6 bg-muted border-muted/50">
        <CardContent>
          <h2 className="text-xl font-semibold mb-2">What would you like to learn?</h2>
          <textarea
            value={prompt}
            onChange={handlePromptChange}
            title="Learning topic"
            placeholder="Enter your topic here, and we'll generate a deck of flashcards for you."
            className="w-full bg-white text-base p-4 rounded border border-gray-200 shadow-sm mb-4 resize-y"
            rows={4}
          />
          <div className="flex flex-col sm:flex-row justify-start gap-2 sm:items-end">
            {generationState !== "start" ? (
              <>
                <Button onClick={saveDeck} disabled={!deckName}>
                  SAVE DECK
                </Button>
                <Button onClick={generateDeck} variant="muted">
                  REGENERATE DECK
                </Button>
                <Button onClick={resetDeck} variant="muted">
                  RESET
                </Button>
              </>
            ) : (
              <Button onClick={generateDeck} disabled={!prompt}>
                GENERATE DECK
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
      {generationState === "generated" && (
        <>
          <div className="flex justify-between items-center mb-4">
            <textarea
              value={deckName}
              onChange={handleDeckNameChange}
              title="Deck name"
              placeholder="Deck name"
              className="text-2xl font-bold w-full resize-none outline-none bg-transparent border border-transparent hover:border-gray-200 rounded transition-all -ml-2 pl-2 py-1"
              style={{ overflow: "hidden", cursor: "text" }}
              rows={1}
            />
          </div>
          <h2 className="text-xl font-semibold max-sm:mb-4">Cards ({cards.length})</h2>
          <div className="mt-6 p-4 bg-gray-50 rounded">
            <div className="flex flex-wrap gap-4 relative">
              <AnimatePresence initial={false} mode="popLayout">
                {cards.map((cardProps) => (
                  <GeneratedCard
                    key={cardProps.tempId}
                    {...cardProps}
                    updateQuestion={updateQuestion}
                    updateAnswer={updateAnswer}
                    deleteCard={deleteCard}
                  />
                ))}
              </AnimatePresence>
            </div>
          </div>
        </>
      )}
      {generationState === "error" && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 text-red-600 rounded">
          <h3 className="font-semibold">Error generating deck</h3>
          <pre className="whitespace-pre-wrap">{generationError}</pre>
        </div>
      )}
      {generationState === "generating" && (
        <div className="flex-1 flex items-center justify-center">
          <Spinner />
        </div>
      )}
    </div>
  );
};
